/**
 * Colours for the canvas chart.
 *
 * Hard-coded hex rather than read from the CSS custom properties, deliberately:
 * the design tokens are in oklch(), and canvas support for CSS Color 4 syntax is
 * still uneven across browsers. A chart that silently renders black because a
 * colour failed to parse is worse than a small amount of duplication.
 *
 * These are matched by eye to the oklch tokens in index.css.
 */
export interface ChartPalette {
  background: string;
  text: string;
  /**
   * Background grid, at half the strength it originally had.
   *
   * rgba rather than a lighter flat hex: the chart background is transparent,
   * so the grid blends against whatever surface the card is on. A pre-blended
   * hex would be right on one theme and wrong on the other.
   */
  grid: string;
  border: string;
  crosshair: string;
  bull: string;
  bear: string;
  /** Unfilled gap lines. */
  gap: string;
  gapUp: string;
  gapDown: string;
  /*
   * Gap lines by age: today, within the last seven days, older.
   *
   * Age rather than direction, which the chart used to colour by. Which way a
   * gap points is already obvious from where it sits relative to price, while
   * how fresh it is is not — and that is what decides whether it is worth
   * watching.
   *
   * Today is red and everything earlier is blue; the week and older bands
   * share that blue deliberately, so the chart reads as one split — today or
   * not — rather than a gradient the eye has to rank. They stay separate
   * tokens so the two can be told apart again without touching the primitive.
   */
  gapToday: string;
  gapWeek: string;
  gapOlder: string;
  /** RSI line. */
  rsi: string;
  /** Volume profile bars, inside and outside the value area. */
  volumeBar: string;
  volumeValue: string;
  /** Point of control, value-area edges, and the two node kinds. */
  volumePoc: string;
  volumeEdge: string;
  volumeLvn: string;
  volumeHvn: string;
  /** The most recently taken knot; the rest get generated hues. */
  knotLatest: string;
  /** Lightness for those generated hues, tuned to the chart background. */
  knotLightness: number;
  /** Knots waiting on their confirming candle, drawn dashed. */
  knotPending: string;
  /** Armed price-alert lines, and ones that have fired or are switched off. */
  alert: string;
  alertMuted: string;
  /** Fibonacci swings, one colour per timeframe they are drawn on. */
  fib: Record<string, string>;
  /** The owner's hand-drawn teaching Fibonaccis. */
  fibTeaching: string;
}

const dark: ChartPalette = {
  // Transparent, so the chart sits on its glass card rather than a slab.
  background: 'transparent',
  text: '#9aa1ae',
  grid: 'rgba(38, 41, 50, 0.5)',
  border: '#333744',
  crosshair: '#6b7280',
  bull: '#22c48b',
  bear: '#f0554f',
  gap: '#facc15',
  gapUp: '#fde047',
  gapDown: '#fbbf24',
  gapToday: '#f87171',
  gapWeek: '#60a5fa',
  gapOlder: '#60a5fa',
  rsi: '#a78bfa',
  // Translucent so the candles stay readable through the histogram: the profile
  // is context for the price action, not a layer competing with it.
  volumeBar: 'rgba(125, 133, 150, 0.20)',
  volumeValue: 'rgba(56, 189, 248, 0.26)',
  volumePoc: '#38bdf8',
  volumeEdge: '#0ea5e9',
  volumeLvn: '#f472b6',
  volumeHvn: '#64748b',
  knotLatest: '#f59e0b',
  knotLightness: 62,
  knotPending: '#38bdf8',
  alert: '#fb923c',
  alertMuted: '#6b7280',
  fib: { M1: '#22d3ee', M15: '#e879f9', H1: '#fbbf24', H4: '#fb7185', D1: '#a3e635' },
  fibTeaching: '#f8fafc',
};

const light: ChartPalette = {
  background: 'transparent',
  text: '#5c6270',
  grid: 'rgba(236, 238, 242, 0.5)',
  border: '#d8dbe2',
  crosshair: '#9ca3af',
  bull: '#12a06a',
  bear: '#dc3d38',
  gap: '#c98a04',
  gapUp: '#b8860b',
  gapDown: '#d97706',
  gapToday: '#dc2626',
  gapWeek: '#2563eb',
  gapOlder: '#2563eb',
  rsi: '#7c3aed',
  volumeBar: 'rgba(100, 116, 139, 0.18)',
  volumeValue: 'rgba(2, 132, 199, 0.20)',
  volumePoc: '#0284c7',
  volumeEdge: '#0369a1',
  volumeLvn: '#db2777',
  volumeHvn: '#64748b',
  knotLatest: '#d97706',
  // Darker hues, because the generated colours sit on a white chart here.
  knotLightness: 42,
  knotPending: '#0284c7',
  alert: '#ea580c',
  alertMuted: '#9ca3af',
  fib: { M1: '#0891b2', M15: '#c026d3', H1: '#d97706', H4: '#e11d48', D1: '#65a30d' },
  fibTeaching: '#111827',
};

export function chartPalette(theme: 'dark' | 'light'): ChartPalette {
  return theme === 'dark' ? dark : light;
}
